import { useNavigate } from 'react-router-dom';
import { facilityStaff, type ApiError, type FacilityStaffMember } from '@aithora/shared';
import { useConsoleRole } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { AboutThis } from '../../ui/AboutThis';
import { tierLabel } from '../residents/common';
import { usePerf } from './ctx';
import { pre, usePerfApi } from './lib';
import { dayLabel } from './surveyVals';
import { NOT_CONNECTED, NotConnected, ReadOnlyLine, SectionHead, V9_BTN, V9_HEAD, V9_PANE, V9_SECTION } from './v9parts';

const RED = '#B3261E';
const OK = '#1F6B52';
const GREY = '#54606C';
const DAY = 86_400_000;

function allowed<T>(p: Promise<T>): Promise<T | null> {
  return p.catch((e: ApiError) => {
    if (e && (e.kind === 'not_found' || e.kind === 'forbidden')) return null;
    throw e;
  });
}

export interface CredRow { id: string; name: string; label: string; issue: string; meta: string }

const VERIFIED: Record<string, string> = { verified_nursys: 'Checked through Nursys', verified_manually: 'Checked by hand', unverified: 'Not checked yet' };

/**
 * The building's licenses and permits on file (GET /facilities/:id/staff, each person's
 * current credential): who cannot work in that role until it is fixed (the server says it
 * counts as none, and why), and what ends within 45 days.
 */
export function credentialRows(staff: FacilityStaffMember[], nowMs: number): { blocked: CredRow[]; expiring: CredRow[]; onFile: number; nursys: number; manual: number } {
  const blocked: CredRow[] = [];
  const expiring: CredRow[] = [];
  let onFile = 0;
  let nursys = 0;
  let manual = 0;
  for (const p of staff) {
    const c = p.credential;
    if (!c) continue;
    onFile++;
    if (c.verification === 'verified_nursys') nursys++;
    else if (c.verification === 'verified_manually') manual++;
    const who = p.name ?? 'Name not on file';
    const meta = VERIFIED[c.verification] ?? c.verification;
    if (c.countsAs === 'none') {
      blocked.push({ id: p.id, name: who, label: c.label, issue: c.notCounted ?? 'It does not count in this building.', meta });
      continue;
    }
    if (c.expiresOn) {
      const ends = Date.parse(c.expiresOn + 'T12:00:00Z');
      if (Number.isFinite(ends) && ends - nowMs <= 45 * DAY) expiring.push({ id: p.id, name: who, label: c.label, issue: 'License or permit ends ' + dayLabel(c.expiresOn) + '.', meta });
    }
  }
  return { blocked, expiring, onFile, nursys, manual };
}

/** The lead line (design crLead) from the people who cannot work in their role yet. */
export function credLead(open: number): string {
  return open ? open + (open === 1 ? ' person cannot' : ' people cannot') + ' be scheduled until fixed' : 'No license or permit on file has lapsed';
}

export function credSub(expiring: number): string {
  return (
    'An expired license takes the person off the schedule until it is renewed.' +
    (expiring ? ' ' + expiring + (expiring === 1 ? ' license ends' : ' licenses end') + ' in the next 45 days.' : '') +
    ' Only the licenses entered in Aithora are checked here.'
  );
}

/** Where each check comes from (design crSrc), with what is connected today. */
export function credSources(onFile: number, nursys: number, manual: number): Array<{ k: string; who: string; st: string; ok: boolean }> {
  return [
    { k: 'Nurses (RN, LVN)', who: 'Nursys e-Notify', st: nursys ? nursys + ' of ' + onFile + ' licenses checked through Nursys' : NOT_CONNECTED, ok: nursys > 0 },
    { k: 'Licenses entered in Aithora', who: 'Users & Permissions', st: onFile ? onFile + ' on file · ' + manual + ' checked by hand' : 'None on file yet', ok: onFile > 0 },
    { k: 'Nurse aides', who: 'State nurse aide registry and employee misconduct registry', st: NOT_CONNECTED, ok: false },
    { k: 'Agency staff', who: 'Staffing agencies', st: NOT_CONNECTED, ok: false },
    { k: 'Exclusion lists', who: 'OIG, SAM.gov and the state Medicaid lists', st: NOT_CONNECTED, ok: false },
  ];
}

/**
 * Performance > Credentials (design ConsoleV9 isCredTab, L19008). From the licenses on file
 * in Aithora; registry, exclusion-list and training feeds are not connected yet and say so.
 * The Facility Admin fixes (in Settings > Users & Permissions); Super Admin and Support read.
 */
export function CredentialsTab() {
  const { role } = useConsoleRole();
  const { fa, fac, canAct } = usePerf();
  const navigate = useNavigate();
  const id = fa ? 'me' : fac?.orgId ?? null;
  const q = usePerfApi((c, signal) => allowed(facilityStaff(c, { params: { id: id! }, signal })), [id], !!id);
  const rows = credentialRows(q.data?.staff ?? [], Date.now());
  const act = fa && canAct;
  // The design's two lists (isCredTab): blocked rows lead with the name and a filled button,
  // the issue and its source under it; expiring rows read name and line with an outlined one.
  const fix = (filled: boolean) => (
    <a
      href="#"
      onClick={(e) => {
        pre(e);
        navigate('/settings?section=users-and-permissions');
      }}
      style={sx(filled ? V9_BTN + 'background:#2F5C97;color:#fff;border-color:#2F5C97;' : V9_BTN)}
    >
      Update the license
    </a>
  );
  const who = (b: CredRow) => (
    <span style={sx('flex:1 1 260px;min-width:0;font-size:16px;font-weight:700;')}>
      <span translate="no">{b.name}</span>
      <span>{' · '}</span>
      <span>{b.label}</span>
    </span>
  );
  const blockedList = (empty: string) => (
    <div data-testid="cred-blocked" data-screen-label="Credentials · Blocked" style={sx(V9_SECTION)}>
      <div style={sx('display:flex;align-items:baseline;gap:8px 12px;flex-wrap:wrap;margin-bottom:4px;')}>
        <span style={sx(V9_HEAD + 'color:' + RED + ';')}>Can’t be scheduled until fixed</span>
      </div>
      {rows.blocked.length === 0 ? (
        <NotConnected text={empty} />
      ) : (
        rows.blocked.map((b) => (
          <div key={b.id} style={sx('display:flex;flex-direction:column;gap:3px;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.08);min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;')}>
              {who(b)}
              {act && fix(true)}
            </div>
            <div style={sx('font-size:16px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>{b.issue}</div>
            <div style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{b.meta}</div>
          </div>
        ))
      )}
    </div>
  );
  const expiringList = (empty: string) => (
    <div data-testid="cred-expiring" data-screen-label="Credentials · Expiring" style={sx(V9_SECTION)}>
      <SectionHead title="Expires in the next 45 days" />
      {rows.expiring.length === 0 ? (
        <NotConnected text={empty} />
      ) : (
        rows.expiring.map((x) => (
          <div key={x.id} style={sx('display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.08);')}>
            <div style={sx('flex:1 1 300px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
              {who(x)}
              <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>{x.issue}</span>
              <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{x.meta}</span>
            </div>
            {act && fix(false)}
          </div>
        ))
      )}
    </div>
  );
  const loading = q.loading && !q.data;
  const missing = !loading && !q.data;
  return (
    <div data-screen-label="Performance · Credentials" data-testid="perf-credentials" style={sx(V9_PANE)}>
      <div style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;')}>
        <span style={sx('font-size:18px;font-weight:700;line-height:1.4;text-wrap:pretty;')}>{loading ? 'Loading…' : missing ? 'Licenses are not connected for this building yet' : credLead(rows.blocked.length)}</span>
        <div style={sx('font-size:16px;color:#54606C;line-height:1.4;text-wrap:pretty;max-width:96ch;')}>{credSub(rows.expiring.length)}</div>
      </div>
      {role === 'SUPER_ADMIN' && (
        <div data-screen-label="Credentials · Buildings" data-testid="cred-rank" style={sx(V9_SECTION)}>
          <SectionHead title="Your buildings, most blocked staff first" />
          <NotConnected text="Not connected yet. Each building’s licenses roll up here once the cross-building view is connected." />
          {fac?.name && <div style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-top:6px;')}>{'The detail below is ' + fac.name + '.'}</div>}
        </div>
      )}
      {blockedList(missing ? NOT_CONNECTED : 'Nobody with a license on file is blocked.')}
      {expiringList(missing ? NOT_CONNECTED : 'Nothing on file ends in the next 45 days.')}
      <div data-testid="cred-training" style={sx(V9_SECTION)}>
        <SectionHead title="Required training" note="Federal minimum, 42 CFR 483.95. Surveyors check aide in-service hours under F947." />
        <NotConnected />
      </div>
      <div data-testid="cred-sources" style={sx(V9_SECTION)}>
        <div style={sx('display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;margin-bottom:4px;')}>
          <span style={sx('flex:1 1 auto;' + V9_HEAD)}>Where each check comes from</span>
        </div>
        {credSources(rows.onFile, rows.nursys, rows.manual).map((x) => (
          <div key={x.k} style={sx('display:flex;flex-direction:column;gap:2px;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.08);min-width:0;')}>
            <div style={sx('display:flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;')}>
              <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;font-weight:700;')}>{x.k}</span>
              <span style={sx('font-size:14px;font-weight:700;color:' + (x.ok ? OK : GREY) + ';')}>{x.st}</span>
            </div>
            <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{x.who}</span>
          </div>
        ))}
        <AboutThis inline textStyle="font-size:14px;color:#54606C;line-height:1.4;margin-top:6px;">
          No single national license check exists. Nurses verify through Nursys, which alerts us when a license changes. Aides verify through each state’s registry where it allows a connection, otherwise by upload. Exclusion lists are checked at hire and every month for staff, contractors and agency workers, as OIG expects; surveyors and auditors ask for the record.
        </AboutThis>
      </div>
      {!act && <ReadOnlyLine text={'Read-only as ' + tierLabel(role)} />}
    </div>
  );
}
